import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';

/**
 * TextSelectionTrigger — 划词操作工具栏
 *
 * 用户选中文字后显示浮动按钮：引用｜提问｜AI讨论
 * - 引用：仅出现在帖子类内容中，将选中文本以引用格式插入编辑器
 * - 提问：打开 taro-ask 费用确认弹窗，创建提问
 * - AI讨论：打开 Sarah AI 聊天面板
 */
export default class TextSelectionTrigger extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.showing = false;
    this.position = { x: 0, y: 0 };
    this.selectedText = '';
    this.selectedTextDom = '';
    this.checking = false;
    this.selectionDebounce = null;
    this.boundOnSelectionChange = this.onSelectionChange.bind(this);
    this.boundOnOutsideClick = this.onOutsideClick.bind(this);
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    document.addEventListener('selectionchange', this.boundOnSelectionChange);
    document.addEventListener('mousedown', this.boundOnOutsideClick);
  }

  onremove(vnode) {
    super.onremove(vnode);
    document.removeEventListener('selectionchange', this.boundOnSelectionChange);
    document.removeEventListener('mousedown', this.boundOnOutsideClick);
    if (this.selectionDebounce) clearTimeout(this.selectionDebounce);
  }

  onSelectionChange() {
    if (this.selectionDebounce) clearTimeout(this.selectionDebounce);
    this.selectionDebounce = setTimeout(() => this.onSelection(), 100);
  }

  canUseSelection() {
    const user = app.session.user;
    if (!user) return false;
    const level = user.attribute('taroSwordLevel') || 'S0';
    if (level === 'S0') return false;

    const configs = app.forum.attribute('taroAiLevelConfigs') || {};
    return configs[level]?.selectionEnabled !== false;
  }

  /** 判断选中文本是否在帖子类容器中 */
  isSelectionInPost() {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return false;
    const range = sel.getRangeAt(0);
    return !!(range.commonAncestorContainer.closest
      ? range.commonAncestorContainer.closest('.Post')
      : false);
  }

  /** 获取当前讨论 ID */
  getDiscussionId() {
    // 优先从当前页面数据获取
    const disc = app.current?.data?.discussion;
    if (disc && disc.id()) return disc.id();

    // 回退：从选中文本所在 DOM 查找
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return null;
    const range = sel.getRangeAt(0);
    const postEl = range.commonAncestorContainer.closest
      ? range.commonAncestorContainer.closest('.Post')
      : null;
    if (postEl && postEl.dataset.id) {
      const post = app.store.getById('posts', postEl.dataset.id);
      if (post && post.discussion()) return post.discussion().id();
    }
    return null;
  }

  /** 获取当前帖子 ID */
  getPostId() {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return null;
    const range = sel.getRangeAt(0);
    const postEl = range.commonAncestorContainer.closest
      ? range.commonAncestorContainer.closest('.Post')
      : null;
    if (postEl && postEl.dataset.id) {
      return postEl.dataset.id;
    }
    return null;
  }

  onSelection() {
    if (this.checking) return;
    this.checking = true;
    setTimeout(() => { this.checking = false; }, 200);

    if (!this.canUseSelection()) return;

    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) {
      this.showing = false;
      m.redraw();
      return;
    }

    const text = sel.toString().trim();
    if (text.length < 3 || text.length > 500) {
      this.showing = false;
      return;
    }

    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    this.selectedText = text;
    this.position = {
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    };
    this.showing = true;
    m.redraw();
  }

  onOutsideClick(e) {
    if (this.showing && this.element && !this.element.contains(e.target)) {
      this.showing = false;
      m.redraw();
    }
  }

  /** 引用 — 插入引用格式到编辑器 */
  quoteSelection() {
    const quoteText = this.extractQuoteText();
    if (!quoteText) return;

    const sel = window.getSelection();
    const range = sel.getRangeAt(0);
    const postEl = range.commonAncestorContainer.closest
      ? range.commonAncestorContainer.closest('.Post')
      : null;

    let quoteBlock;
    if (postEl) {
      const post = app.store.getById('posts', postEl.dataset.id);
      const authorName = post && post.user()
        ? post.user().displayName()
        : '未知用户';
      quoteBlock = '> **' + authorName + '** 说：\n\n> ' + quoteText.replace(/\n/g, '\n> ');
    } else {
      quoteBlock = '> ' + quoteText.replace(/\n/g, '\n> ');
    }

    if (app.composer.isVisible()) {
      app.composer.editor.insertAtCursor('\n' + quoteBlock + '\n\n');
    } else {
      navigator.clipboard.writeText(quoteBlock).catch(() => {});
      alert('已复制引用内容到剪贴板');
    }

    this.showing = false;
    m.redraw();
  }

  /** AI讨论 — 将划词引用预填到 Sarah AI 输入框，不自动发送 */
  openAiDiscussion() {
    const fullText = this.selectedText;
    // 缩略引用：只显示前 20 个字符 + 省略号，用于输入框视觉标记
    const shortText = fullText.length > 20 ? fullText.slice(0, 20) + '…' : fullText;
    const quoteLine = '> 「' + shortText + '」';

    app.sarahAi.initialQuery = quoteLine;
    app.sarahAi.selectedTextFull = fullText;
    app.sarahAi.autoSend = false;
    app.sarahAi.fromSelection = true;
    app.sarahAi.panelOpen = true;
    this.showing = false;
    m.redraw();
  }

  /** 提问 — 打开 taro-ask 费用确认弹窗 */
  openAskQuestion() {
    const discussionId = this.getDiscussionId();
    if (!discussionId) {
      alert('请在讨论页面中使用此功能');
      this.showing = false;
      m.redraw();
      return;
    }

    if (app.askQuestionModal && typeof app.askQuestionModal.open === 'function') {
      app.askQuestionModal.open({
        selectedText: this.selectedText,
        discussionId,
        postId: this.getPostId(),
      });
    } else {
      // taro-ask 可能尚未加载
      alert('提问功能暂不可用');
    }

    this.showing = false;
    m.redraw();
  }

  /** 提取选中文本的纯文本内容 */
  extractQuoteText() {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return '';

    const range = sel.getRangeAt(0);
    const div = document.createElement('div');
    div.appendChild(range.cloneContents());

    div.querySelectorAll('img').forEach(img => {
      if (img.classList.contains('emoji')) {
        img.replaceWith(img.alt || '');
      } else {
        img.replaceWith(img.alt ? '![' + img.alt + '](' + img.src + ')' : '');
      }
    });
    div.querySelectorAll('a').forEach(a => {
      a.replaceWith(a.textContent);
    });

    return div.textContent || '';
  }

  view() {
    if (!this.showing) return null;

    const inPost = this.isSelectionInPost();
    const hasEditor = app.composer && app.composer.isVisible();
    const showQuote = inPost && hasEditor;
    const taroAskEnabled = app.forum.attribute('taroAskEnabled');

    return (
      <div
        className="TextSelectionTrigger"
        style={{
          position: 'fixed',
          left: this.position.x + 'px',
          top: this.position.y + 'px',
          transform: 'translate(-50%, -100%)',
          zIndex: 1001,
        }}
      >
        <div className="TextSelectionTrigger-toolbar">
          {showQuote && (
            <button
              className="Button TextSelectionTrigger-btn TextSelectionTrigger-btn--quote"
              onclick={() => this.quoteSelection()}
              title="引用选中文本"
            >
              <i className="fas fa-quote-left"></i>
              <span>引用</span>
            </button>
          )}
          {taroAskEnabled && (
            <button
              className="Button TextSelectionTrigger-btn TextSelectionTrigger-btn--ask"
              onclick={() => this.openAskQuestion()}
              title="发起提问（需消耗星币）"
            >
              <i className="fas fa-question-circle"></i>
              <span>提问</span>
            </button>
          )}
          <button
            className="Button Button--primary TextSelectionTrigger-btn TextSelectionTrigger-btn--ai"
            onclick={() => this.openAiDiscussion()}
            title="与 AI 讨论选中内容"
          >
            <i className="fas fa-robot"></i>
            <span>与萨拉聊聊</span>
          </button>
        </div>
      </div>
    );
  }
}
